/**
 * Resolve brandId/brandName from API payloads where brand may appear at the
 * venue top level or on a nested `business` object (DEV-495 contract).
 * Autocomplete suggestions may still use legacy snake_case `brand_id`.
 */
export type BrandFieldSource =
  | {
      brandId?: string | number | null;
      brandName?: string | null;
      brand_id?: string | number | null;
      brand_name?: string | null;
      business?: BrandFieldSource | null;
    }
  | null
  | undefined;

function readBrandId(source: BrandFieldSource): string | number | null | undefined {
  if (!source) return undefined;
  return (
    source.brandId ??
    source.brand_id ??
    source.business?.brandId ??
    source.business?.brand_id
  );
}

function readBrandName(source: BrandFieldSource): string | null | undefined {
  if (!source) return undefined;
  return (
    source.brandName ??
    source.brand_name ??
    source.business?.brandName ??
    source.business?.brand_name
  );
}

/** Canonical camelCase brand fields for app/types layer. */
export function resolveBrandFields(source: BrandFieldSource): {
  brandId: string;
  brandName: string;
} {
  const rawId = readBrandId(source);
  const rawName = readBrandName(source);
  return {
    brandId: rawId != null && rawId !== "" ? String(rawId) : "",
    brandName: rawName ?? "",
  };
}

/** Mixpanel event property keys (tracking-data contract). */
export function brandEventProps(source: BrandFieldSource): {
  brand_id: string;
  brand_name: string;
} {
  const { brandId, brandName } = resolveBrandFields(source);
  return { brand_id: brandId, brand_name: brandName };
}
